import { useEffect, useMemo, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import logger from '../utils/logger'

let channelCounter = 0

/**
 * Creates a debounced invalidation function that coalesces rapid-fire
 * realtime events into a single invalidation after a short delay.
 */
function makeDebouncedInvalidator(queryClient, delayMs = 300) {
  const pending = new Map()

  return (queryKey) => {
    const keyStr = JSON.stringify(queryKey)
    if (pending.has(keyStr)) clearTimeout(pending.get(keyStr))
    pending.set(
      keyStr,
      setTimeout(() => {
        pending.delete(keyStr)
        queryClient.invalidateQueries({ queryKey, refetchType: 'all' })
      }, delayMs)
    )
  }
}

const MAX_RETRIES = 5
const BASE_DELAY_MS = 1000

/**
 * Subscribe a channel with automatic retry on TIMED_OUT / CHANNEL_ERROR.
 * Returns an unsubscribe function.
 */
function subscribeWithRetry(channel, { onSubscribed, onClosed, label }) {
  let removed = false
  let retryCount = 0
  let retryTimer = null
  let retrying = false // guard against multiple CHANNEL_ERROR callbacks per failure

  function attemptSubscribe() {
    channel.subscribe((status, err) => {
      if (removed) {
        if (status === 'SUBSCRIBED') supabase.removeChannel(channel)
        return
      }

      if (status === 'SUBSCRIBED') {
        retryCount = 0 // reset on success
        retrying = false
        onSubscribed?.()
      }

      if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
        logger.realtime(`Channel error (${label})`, { status, error: err?.message })

        // Guard: a single WebSocket drop can fire multiple CHANNEL_ERROR callbacks
        // in rapid succession. Only act on the first one per failure cycle.
        if (retrying) return
        retrying = true

        if (retryCount < MAX_RETRIES) {
          const delay = BASE_DELAY_MS * Math.pow(2, retryCount)
          retryCount++
          logger.realtime(`Retrying channel ${label} in ${delay}ms`, { attempt: retryCount, maxRetries: MAX_RETRIES })
          retryTimer = setTimeout(() => {
            retrying = false
            if (!removed) {
              supabase.removeChannel(channel)
              // Re-subscribe the same channel object after removing the stale one.
              // Supabase JS v2 requires creating a fresh channel after removal,
              // so we call the provided retry hook instead.
              onClosed?.({ retry: true })
            }
          }, delay)
        } else {
          logger.realtime(`Channel ${label} gave up after ${MAX_RETRIES} retries`)
          logger.capture(new Error(`Realtime channel exhausted retries: ${label}`), {
            label, maxRetries: MAX_RETRIES, lastStatus: status,
          })
          onClosed?.({ retry: false })
        }
      }

      if (status === 'CLOSED' && !removed) {
        onClosed?.({ retry: false })
      }
    })
  }

  attemptSubscribe()

  return () => {
    removed = true
    if (retryTimer) clearTimeout(retryTimer)
    supabase.removeChannel(channel)
  }
}

/**
 * Subscribe to Supabase Realtime for score changes on a specific game.
 * When any score row is inserted/updated/deleted, invalidates the React Query
 * cache for that game so all consumers get fresh data.
 *
 * @param {string|null} gameId - The game ID to subscribe to, or null to skip
 */
export function useRealtimeScores(gameId) {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!gameId) return

    let teardown = null
    let removed = false
    const invalidate = makeDebouncedInvalidator(queryClient)

    function setup() {
      if (removed) return
      const suffix = ++channelCounter

      const channel = supabase
        .channel(`scores:game:${gameId}:${suffix}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'scores',
            filter: `game_id=eq.${gameId}`,
          },
          () => {
            if (!removed) invalidate(['game', gameId])
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'bets',
            filter: `game_id=eq.${gameId}`,
          },
          () => {
            if (!removed) {
              invalidate(['presses', gameId])
              invalidate(['game', gameId])
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'ctp_results',
            filter: `game_id=eq.${gameId}`,
          },
          () => {
            if (!removed) invalidate(['ctpResults', gameId])
          }
        )

      teardown = subscribeWithRetry(channel, {
        label: `game ${gameId}`,
        onClosed({ retry }) {
          if (!removed) {
            // Always invalidate so we at least get fresh data via HTTP
            queryClient.invalidateQueries({ refetchType: 'all', queryKey: ['game', gameId] })
            if (retry) setup()
          }
        },
      })
    }

    setup()

    return () => {
      removed = true
      teardown?.()
    }
  }, [gameId, queryClient])
}

/**
 * Subscribe to Supabase Realtime for score/bet/ctp changes across all
 * in-progress games in a match.  Invalidates the matchGames query so
 * the Money Board refreshes live.
 *
 * @param {string|null} matchId
 * @param {string[]} gameIds - IDs of in-progress games to watch
 */
export function useRealtimeMatchScores(matchId, gameIds) {
  const queryClient = useQueryClient()

  // Stabilize the dependency — only re-subscribe when the actual set of IDs changes,
  // not when matchGames refetch produces a new array reference with the same IDs
  const gameIdsKey = useMemo(
    () => (gameIds || []).slice().sort().join(','),
    [gameIds]
  )

  useEffect(() => {
    if (!matchId || !gameIdsKey) return

    let removed = false
    const ids = gameIdsKey.split(',')
    const invalidate = makeDebouncedInvalidator(queryClient)
    const teardowns = []

    function setupChannel(gid) {
      if (removed) return
      const suffix = ++channelCounter

      const channel = supabase
        .channel(`money:${matchId}:${gid}:${suffix}`)
        .on('postgres_changes', { event: '*', schema: 'public', table: 'scores', filter: `game_id=eq.${gid}` }, () => {
          if (!removed) invalidate(['matchGames', matchId])
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'bets', filter: `game_id=eq.${gid}` }, () => {
          if (!removed) invalidate(['matchGames', matchId])
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'ctp_results', filter: `game_id=eq.${gid}` }, () => {
          if (!removed) invalidate(['matchGames', matchId])
        })
        .on('postgres_changes', { event: '*', schema: 'public', table: 'settlements', filter: `game_id=eq.${gid}` }, () => {
          if (!removed) {
            invalidate(['matchGames', matchId])
            invalidate(['settlements'])
          }
        })

      const td = subscribeWithRetry(channel, {
        label: `match ${matchId} game ${gid}`,
        onClosed({ retry }) {
          if (!removed) {
            queryClient.invalidateQueries({ refetchType: 'all', queryKey: ['matchGames', matchId] })
            if (retry) setupChannel(gid)
          }
        },
      })
      teardowns.push(td)
    }

    for (const gid of ids) {
      setupChannel(gid)
    }

    return () => {
      removed = true
      teardowns.forEach((td) => td?.())
    }
  }, [matchId, gameIdsKey, queryClient])
}

/**
 * Subscribe to Realtime for any game changes within a match.
 * Useful for the match dashboard to see when rounds change status.
 *
 * @param {string|null} matchId - The match ID, or null to skip
 */
export function useRealtimeMatch(matchId) {
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!matchId) return

    let teardown = null
    let removed = false
    const invalidate = makeDebouncedInvalidator(queryClient)

    function setup() {
      if (removed) return
      const suffix = ++channelCounter

      const channel = supabase
        .channel(`match:${matchId}:${suffix}`)
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'games',
            filter: `match_id=eq.${matchId}`,
          },
          () => {
            if (!removed) {
              invalidate(['games'])
              invalidate(['match', matchId])
              invalidate(['matchGames', matchId])
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: '*',
            schema: 'public',
            table: 'side_bets',
            filter: `match_id=eq.${matchId}`,
          },
          () => {
            if (!removed) {
              invalidate(['sideBets', matchId])
              invalidate(['matchGames', matchId])
            }
          }
        )

      teardown = subscribeWithRetry(channel, {
        label: `match ${matchId}`,
        onClosed({ retry }) {
          if (!removed) {
            queryClient.invalidateQueries({ refetchType: 'all', queryKey: ['match', matchId] })
            queryClient.invalidateQueries({ refetchType: 'all', queryKey: ['matchGames', matchId] })
            if (retry) setup()
          }
        },
      })
    }

    setup()

    return () => {
      removed = true
      teardown?.()
    }
  }, [matchId, queryClient])
}
